import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Paper,
  IconButton,
  CircularProgress
} from '@mui/material';
import { QrCodeIcon, KeyboardIcon, CloseIcon } from 'icons/materialIcons';
import { useNavigate } from 'react-router-dom';
import ScannerErrorBoundary from 'utils/ScannerErrorBoundary';

const AccessoryLanding = () => {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [serialNumber, setSerialNumber] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [scannerMessage, setScannerMessage] = useState('');
  const [scannerLoading, setScannerLoading] = useState(false);
  const [scannerState, setScannerState] = useState('idle');

  const scannerContainerRef = useRef(null);
  const scannerRef = useRef(null);
  const scannerId = 'qr-reader-container';

  const handleScanClick = () => {
    setSerialNumber('');
    setScannerMessage('');
    setTabValue(0);
    setScannerLoading(true);
    setDialogOpen(true);
  };

  const destroyScanner = () => {
    if (window.currentScanner) {
      try {
        if (typeof window.currentScanner.stop === 'function') {
          window.currentScanner.stop();
        }

        setTimeout(() => {
          try {
            window.currentScanner.clear();
            window.currentScanner = null;
          } catch (err) {
            window.currentScanner = null;
          }
          setScannerState('idle');
        }, 200);
      } catch (err) {
        window.currentScanner = null;
        setScannerState('idle');
      }
    } else {
      setScannerState('idle');
    }
  };

  const handleDialogClose = () => {
    destroyScanner();
    setTimeout(() => {
      setDialogOpen(false);
    }, 250);
  };

  const handleQrCodeSuccess = (decodedText) => {
    let scannedSerialNumber = decodedText;
    let isLegacy = false;

    const legacyPatterns = [
      /intouchwe\.com\/view-profile\/([A-Za-z0-9-]+)/i,
      /app\.intouchwe\.com\/view-profile\/([A-Za-z0-9-]+)/i,
      /www\.intouchwe\.com\/view-profile\/([A-Za-z0-9-]+)/i
    ];

    for (const pattern of legacyPatterns) {
      const match = decodedText.match(pattern);
      if (match && match[1]) {
        scannedSerialNumber = match[1];
        isLegacy = true;
        break;
      }
    }

    if (!isLegacy) {
      const urlMatch = decodedText.match(/\/accessory\/scan\/([A-Za-z0-9-]+)/);
      if (urlMatch && urlMatch[1]) {
        scannedSerialNumber = urlMatch[1];
      }
    }

    setScannerMessage(`Success! ${isLegacy ? 'Legacy ID' : 'Serial number'}: ${scannedSerialNumber}`);

    setTimeout(() => {
      destroyScanner();
      handleDialogClose();
      const queryParams = isLegacy ? '?isLegacy=true' : '';
      navigate(`/accessory/scan/${scannedSerialNumber}${queryParams}`);
    }, 1000);
  };

  const handleMockScan = () => {
    if (serialNumber) {
      const isLegacy = /^[A-Z0-9]{5,7}$/.test(serialNumber);
      const queryParams = isLegacy ? '?isLegacy=true' : '';

      handleDialogClose();
      navigate(`/accessory/scan/${serialNumber}${queryParams}`);
    }
  };

  const handleTabChange = (event, newValue) => {
    if (scannerState === 'initializing' || scannerState === 'cleaning') {
      setScannerMessage('Please wait until scanner is ready before switching tabs...');
      return;
    }

    if (newValue !== 0) {
      setScannerState('cleaning');
      destroyScanner();
    }

    setTabValue(newValue);
  };

  useEffect(() => {
    if (!dialogOpen || tabValue !== 0) {
      destroyScanner();
      return;
    }

    setScannerLoading(true);
    setScannerState('initializing');

    const timer = setTimeout(() => {
      destroyScanner();

      const container = document.getElementById(scannerId);
      if (!container) {
        setScannerLoading(false);
        setScannerMessage("Scanner container not found. Please try again.");
        setScannerState('idle');
        return;
      }

      container.innerHTML = '';

      try {
        import('html5-qrcode').then(({ Html5QrcodeScanner }) => {
          try {
            const config = {
              fps: 10,
              qrbox: { width: 250, height: 250 },
              rememberLastUsedCamera: true,
            };

            window.currentScanner = new Html5QrcodeScanner(
              scannerId,
              config,
              false
            );

            window.currentScanner.render(
              handleQrCodeSuccess,
              (errorMessage) => {
              }
            );

            setScannerLoading(false);
            setScannerState('ready');
          } catch (error) {
            setScannerMessage("Could not initialize camera. Please use manual entry or check camera permissions.");
            setScannerLoading(false);
            setScannerState('idle');
          }
        }).catch(err => {
          setScannerLoading(false);
          setScannerMessage("Failed to load scanner library. Please try again or use manual entry.");
          setScannerState('idle');
        });
      } catch (error) {
        setScannerMessage("Could not set up camera. Please use manual entry.");
        setScannerLoading(false);
        setScannerState('idle');
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      destroyScanner();
    };
  }, [dialogOpen, tabValue, navigate]);

  useEffect(() => {
    return () => {
      destroyScanner();
    };
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        textAlign: 'center',
        backgroundColor: '#f5f5f5'
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 'bold',
          mb: 2
        }}
      >
        Welcome to Accessories
      </Typography>

      <Typography
        variant="body1"
        sx={{
          mb: 4,
          maxWidth: '600px',
          color: '#666'
        }}
      >
        Scan your accessory's QR code to begin the activation process or view your digital card.
      </Typography>

      <Button
        variant="contained"
        startIcon={<QrCodeIcon />}
        onClick={handleScanClick}
        sx={{
          minWidth: '200px',
          padding: '12px 24px',
          fontSize: '1.1rem',
          backgroundColor: '#000',
          borderRadius: '8px',
          '&:hover': {
            backgroundColor: '#333'
          }
        }}
      >
        Scan Accessory
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Scan Accessory</Typography>
            <IconButton onClick={handleDialogClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Paper sx={{ mb: 2 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="fullWidth"
              indicatorColor="primary"
              textColor="primary"
              centered
            >
              <Tab
                icon={<QrCodeIcon />}
                label="Scan QR Code"
                iconPosition="start"
              />
              <Tab
                icon={<KeyboardIcon />}
                label="Enter Serial Number"
                iconPosition="start"
              />
            </Tabs>
          </Paper>

          {tabValue === 0 ? (
            <ScannerErrorBoundary>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minHeight: '300px',
                  width: '100%'
                }}
              >
                <div style={{ width: '100%' }}>
                  <div id={scannerId} style={{ width: '100%' }}></div>
                  {scannerLoading && (
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '300px',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.7)',
                      zIndex: 1
                    }}>
                      <CircularProgress size={40} sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Initializing camera...
                      </Typography>
                    </Box>
                  )}
                </div>
                {scannerMessage && (
                  <Typography
                    variant="body2"
                    color={
                      scannerMessage.includes("Success") ?
                        "success.main" :
                        scannerMessage.includes("Please wait until scanner") ?
                          "info.main" :
                          "error"
                    }
                    sx={{
                      mt: 2,
                      fontWeight: scannerMessage.includes("Success") ? 'bold' : 'normal'
                    }}
                  >
                    {scannerMessage}
                  </Typography>
                )}
              </Box>
            </ScannerErrorBoundary>
          ) : (
            <Box sx={{ py: 3 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Enter the serial number found on your accessory:
              </Typography>
              <TextField
                autoFocus
                fullWidth
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                placeholder="Enter serial number"
                variant="outlined"
              />
            </Box>
          )}
        </DialogContent>

        {tabValue === 1 && (
          <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
            <Button
              onClick={handleDialogClose}
              variant="outlined"
              sx={{
                borderColor: '#ddd',
                color: '#666',
                '&:hover': {
                  borderColor: '#bbb',
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMockScan}
              variant="contained"
              disabled={!serialNumber}
              sx={{
                backgroundColor: '#000',
                '&:hover': {
                  backgroundColor: '#333'
                }
              }}
            >
              Continue
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

export default AccessoryLanding;